import { useState } from 'react';
import { LOGO_URL } from '@/lib/constants';

interface MenuItem {
  id: string;
  label: string;
  icon: string;
}

const menuItems: MenuItem[] = [
  { id: 'general', label: 'Genel Ayarlar', icon: 'ri-settings-3-line' },
  { id: 'navbar', label: 'Navigasyon', icon: 'ri-menu-line' },
  { id: 'slider', label: 'Hero Slider', icon: 'ri-slideshow-line' },
  { id: 'features', label: 'Özellikler Şeridi', icon: 'ri-star-line' },
  { id: 'story', label: 'Hikayemiz', icon: 'ri-book-open-line' },
  { id: 'newsletter', label: 'Bülten Alanı', icon: 'ri-mail-line' },
  { id: 'testimonials', label: 'Müşteri Yorumları', icon: 'ri-chat-quote-line' },
  { id: 'campaign', label: 'Kampanya Bannerları', icon: 'ri-megaphone-line' },
  { id: 'contact', label: 'İletişim Bilgileri', icon: 'ri-phone-line' },
  { id: 'social', label: 'Sosyal Medya', icon: 'ri-share-line' },
  { id: 'footer', label: 'Footer', icon: 'ri-layout-bottom-line' },
  { id: 'pages', label: 'Sayfalar', icon: 'ri-pages-line' },
];

interface Props {
  activeSection: string;
  onSectionChange: (id: string) => void;
  onLogout: () => void;
  children: React.ReactNode;
}

export default function TemaLayout({ activeSection, onSectionChange, onLogout, children }: Props) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const activeItem = menuItems.find(m => m.id === activeSection);

  return (
    <div className="min-h-screen bg-stone-100 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-stone-900 flex flex-col transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static lg:flex`}>
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-5 border-b border-stone-700">
          <img src={LOGO_URL} alt="Logo" className="h-8 w-auto object-contain" />
          <div>
            <p className="text-xs font-semibold text-white">Tema Paneli</p>
            <p className="text-xs text-stone-400">Site Yönetimi</p>
          </div>
        </div>

        {/* Menu */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          {menuItems.map(item => (
            <button
              key={item.id}
              onClick={() => { onSectionChange(item.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap ${
                activeSection === item.id
                  ? 'bg-amber-600 text-white font-medium'
                  : 'text-stone-400 hover:text-white hover:bg-stone-800'
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={item.icon} />
              </div>
              {item.label}
            </button>
          ))}
        </nav>

        {/* Bottom */}
        <div className="p-4 border-t border-stone-700 space-y-2">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-stone-400 hover:text-white hover:bg-stone-800 transition-all cursor-pointer whitespace-nowrap"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-external-link-line" />
            </div>
            Siteyi Görüntüle
          </a>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-stone-400 hover:text-red-400 hover:bg-stone-800 transition-all cursor-pointer whitespace-nowrap"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-logout-box-line" />
            </div>
            Çıkış Yap
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="bg-white border-b border-stone-200 px-4 lg:px-8 py-4 flex items-center gap-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-xl hover:bg-stone-100 transition-colors cursor-pointer"
          >
            <i className="ri-menu-line text-stone-600" />
          </button>
          <div>
            <h1 className="text-base font-semibold text-stone-800">{activeItem?.label}</h1>
            <p className="text-xs text-stone-400">Değişiklikler otomatik kaydedilir</p>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
